import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip, Warning } from '../../components/LessonUI';

const HTMLLayout = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Semantic HTML5</div>
                <h1>Semantic Layout (header, main, footer)</h1>
                <p className="hero-lead">
                    Learn to structure pages with HTML5 semantic elements. Proper layout improves SEO, accessibility,
                    and code maintainability by giving meaning to your page structure.
                </p>
            </div>

            <div className="content-section">
                <h2>Why Semantic Layout Matters</h2>
                <p>
                    Semantic HTML uses elements that clearly describe their meaning and purpose, making your code
                    more readable for humans and machines (browsers, search engines, screen readers).
                </p>

                <div className="benefits-grid">
                    <div className="benefit-card">
                        <h3> Better SEO</h3>
                        <p>Search engines understand page structure and give semantic elements more weight in rankings</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Improved Accessibility</h3>
                        <p>Screen readers use semantic elements as landmarks, helping users navigate efficiently</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Code Readability</h3>
                        <p>Developers can instantly understand page structure without reading all the content</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Easier Maintenance</h3>
                        <p>Clear structure makes it easier to update and maintain code over time</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>The Problem with Non-Semantic HTML</h2>
                <p>
                    Before HTML5, developers used generic <code>&lt;div&gt;</code> elements for everything:
                </p>

                <div className="code-example error">
                    <div className="code-header">
                        <span className="incorrect"> Non-Semantic (Old Way)</span>
                    </div>
                    <CodeBlock language="html" code={`<div id="header">
    <div id="logo">My Website</div>
    <div id="nav">
        <div class="nav-item"><a href="/">Home</a></div>
        <div class="nav-item"><a href="/about">About</a></div>
    </div>
</div>

<div id="content">
    <div class="post">
        <div class="post-title">Article Title</div>
        <div class="post-body">Article content...</div>
    </div>
</div>

<div id="sidebar">
    <div class="widget">Related Links</div>
</div>

<div id="footer">
    <div> 2025 My Website</div>
</div>`} />
                </div>

                <Warning><strong>Problems:</strong>
                    <ul>
                        <li>No semantic meaningeverything is just a "div"</li>
                        <li>Search engines can't distinguish header from footer</li>
                        <li>Screen readers can't create a document outline</li>
                        <li>Requires reading all IDs/classes to understand structure</li>
                    </ul></Warning>
            </div>

            <div className="content-section">
                <h2>HTML5 Semantic Elements</h2>
                <p>
                    HTML5 introduced semantic elements that describe their purpose:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span className="correct">Semantic (Modern Way)</span>
                    </div>
                    <CodeBlock language="html" code={`<header>
    <h1>My Website</h1>
    <nav>
        <ul>
            <li><a href="/">Home</a></li>
            <li><a href="/about">About</a></li>
        </ul>
    </nav>
</header>

<main>
    <article>
        <h2>Article Title</h2>
        <p>Article content...</p>
    </article>
</main>

<aside>
    <h3>Related Links</h3>
</aside>

<footer>
    <p>&copy; 2025 My Website</p>
</footer>`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Benefits:</strong> Clear structure, search engines understand page hierarchy,
                        screen readers can jump between sections, code is self-documenting.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>The <code>&lt;header&gt;</code> Element</h2>
                <p>
                    The <code>&lt;header&gt;</code> represents introductory content for a page or section.
                    It typically contains logos, navigation, headings, and search forms.
                </p>

                <div className="element-details">
                    <h3>Key Points:</h3>
                    <ul>
                        <li>Can be used multiple times (page header + section headers)</li>
                        <li>Usually contains <code>&lt;h1&gt;</code>-<code>&lt;h6&gt;</code>, <code>&lt;nav&gt;</code>, logo</li>
                        <li>Not a sectioning element (doesn't create outline)</li>
                    </ul>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Page Header Example</span>
                    </div>
                    <CodeBlock language="html" code={`<header>
    <div class="logo">
        <img src="logo.svg" alt="Company Logo">
        <h1>My Website</h1>
    </div>
    
    <nav>
        <ul>
            <li><a href="/">Home</a></li>
            <li><a href="/about">About</a></li>
            <li><a href="/services">Services</a></li>
            <li><a href="/contact">Contact</a></li>
        </ul>
    </nav>
    
    <form class="search-form">
        <input type="search" placeholder="Search...">
        <button type="submit">Search</button>
    </form>
</header>`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Article Header Example</span>
                    </div>
                    <CodeBlock language="html" code={`<article>
    <header>
        <h2>Understanding Semantic HTML</h2>
        <p class="meta">
            By John Doe | Published: Jan 15, 2025 | Reading time: 10 min
        </p>
        <img src="featured.jpg" alt="Article cover image">
    </header>
    
    <p>Article content begins here...</p>
</article>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The <code>&lt;main&gt;</code> Element</h2>
                <p>
                    The <code>&lt;main&gt;</code> element represents the primary content of the document.
                    It should contain content unique to this page.
                </p>

                <div className="element-details">
                    <h3>Important Rules:</h3>
                    <ul>
                        <li><strong>Only ONE per page:</strong> Use exactly one <code>&lt;main&gt;</code> element</li>
                        <li><strong>Direct child of <code>&lt;body&gt;</code>:</strong> Don't nest inside <code>&lt;article&gt;</code>, <code>&lt;aside&gt;</code>, etc.</li>
                        <li><strong>Skip repetitive content:</strong> Don't include site navigation, sidebars, or copyright info</li>
                    </ul>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Main Element Example</span>
                    </div>
                    <CodeBlock language="html" code={`<body>
    <header>
        <!-- Site header with logo and nav --&gt;
    </header>

    <main>
        <!-- PRIMARY page content goes here --&gt;
        <h1>Welcome to Our Blog</h1>
        
        <article>
            <h2>First Article</h2>
            <p>Article content...</p>
        </article>
        
        <article>
            <h2>Second Article</h2>
            <p>Article content...</p>
        </article>
    </main>

    <aside>
        <!-- Sidebar content --&gt;
    </aside>

    <footer>
        <!-- Site footer --&gt;
    </footer>
</body>`} />
                </div>

                <div className="tip-box">
                    <strong> Skip Links:</strong> Add a "Skip to main content" link for keyboard/screen reader users:
                    <div className="code-example">
                        <CodeBlock language="html" code={`<a href="#main-content" class="skip-link">Skip to main content</a>
<main id="main-content">
    <!-- Content --&gt;
</main>`} />
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>The <code>&lt;footer&gt;</code> Element</h2>
                <p>
                    The <code>&lt;footer&gt;</code> represents footer information for a page or section.
                    It typically contains copyright, contact info, links, and authorship details.
                </p>

                <div className="element-details">
                    <h3>Common Footer Content:</h3>
                    <ul>
                        <li>Copyright notices</li>
                        <li>Contact information</li>
                        <li>Links to privacy policy, terms of service</li>
                        <li>Social media links</li>
                        <li>Author information (for articles)</li>
                    </ul>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Page Footer Example</span>
                    </div>
                    <CodeBlock language="html" code={`<footer>
    <div class="footer-content">
        <section class="footer-section">
            <h3>About Us</h3>
            <p>Company description and mission statement.</p>
        </section>
        
        <section class="footer-section">
            <h3>Quick Links</h3>
            <ul>
                <li><a href="/about">About</a></li>
                <li><a href="/privacy">Privacy Policy</a></li>
                <li><a href="/terms">Terms of Service</a></li>
            </ul>
        </section>
        
        <section class="footer-section">
            <h3>Contact</h3>
            <p>Email: contact@example.com</p>
            <p>Phone: (555) 123-4567</p>
        </section>
    </div>
    
    <div class="footer-bottom">
        <p>&copy; 2025 My Company. All rights reserved.</p>
        <div class="social-links">
            <a href="https://twitter.com/mycompany">Twitter</a>
            <a href="https://facebook.com/mycompany">Facebook</a>
        </div>
    </div>
</footer>`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Article Footer Example</span>
                    </div>
                    <CodeBlock language="html" code={`<article>
    <h2>Article Title</h2>
    <p>Article content...</p>
    
    <footer>
        <p>
            <strong>Author:</strong> Jane Smith<br>
            <strong>Published:</strong> January 15, 2025<br>
            <strong>Tags:</strong> 
            <a href="/tag/html">HTML</a>, 
            <a href="/tag/tutorial">Tutorial</a>
        </p>
    </footer>
</article>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The <code>&lt;nav&gt;</code> Element</h2>
                <p>
                    The <code>&lt;nav&gt;</code> element represents a section of navigation links.
                </p>

                <div className="element-details">
                    <h3>When to Use <code>&lt;nav&gt;</code>:</h3>
                    <ul>
                        <li>Main site navigation</li>
                        <li>Breadcrumb navigation</li>
                        <li>Table of contents</li>
                        <li>Pagination</li>
                        <li> Footer links (don't need nav, just regular links)</li>
                        <li> Social media icons (not primary navigation)</li>
                    </ul>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Navigation Examples</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- Main navigation --&gt;
<nav aria-label="Main navigation">
    <ul>
        <li><a href="/">Home</a></li>
        <li><a href="/about">About</a></li>
        <li><a href="/services">Services</a></li>
        <li><a href="/contact">Contact</a></li>
    </ul>
</nav>

<!-- Breadcrumb navigation --&gt;
<nav aria-label="Breadcrumb">
    <ol>
        <li><a href="/">Home</a></li>
        <li><a href="/blog">Blog</a></li>
        <li aria-current="page">Current Article</li>
    </ol>
</nav>

<!-- Table of contents --&gt;
<nav aria-label="Table of contents">
    <h2>On this page</h2>
    <ul>
        <li><a href="#intro">Introduction</a></li>
        <li><a href="#main-content">Main Content</a></li>
        <li><a href="#conclusion">Conclusion</a></li>
    </ul>
</nav>`} />
                </div>

                <div className="tip-box">
                    <strong> Accessibility Tip:</strong> Use <code>aria-label</code> when you have multiple <code>&lt;nav&gt;</code>
                    elements to distinguish them for screen readers.
                </div>
            </div>

            <div className="content-section">
                <h2>Complete Page Layout Example</h2>
                <div className="code-example">
                    <div className="code-header">
                        <span>Full Semantic Page Structure</span>
                    </div>
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>My Website</title>
</head>
<body>
    <!-- Skip link for accessibility --&gt;
    <a href="#main-content" class="skip-link">Skip to main content</a>

    <!-- Site header --&gt;
    <header>
        <div class="logo">
            <img src="logo.svg" alt="Company Logo">
            <h1>My Website</h1>
        </div>
        
        <nav aria-label="Main navigation">
            <ul>
                <li><a href="/">Home</a></li>
                <li><a href="/about">About</a></li>
                <li><a href="/blog">Blog</a></li>
                <li><a href="/contact">Contact</a></li>
            </ul>
        </nav>
    </header>

    <!-- Main content area --&gt;
    <main id="main-content">
        <!-- Breadcrumb --&gt;
        <nav aria-label="Breadcrumb">
            <ol>
                <li><a href="/">Home</a></li>
                <li><a href="/blog">Blog</a></li>
                <li aria-current="page">Understanding Semantic HTML</li>
            </ol>
        </nav>

        <!-- Primary article --&gt;
        <article>
            <header>
                <h2>Understanding Semantic HTML</h2>
                <p class="meta">By John Doe | Jan 15, 2025</p>
            </header>
            
            <p>Article content goes here...</p>
            <p>More content...</p>
            
            <footer>
                <p>Tags: <a href="/tag/html">HTML</a>, <a href="/tag/tutorial">Tutorial</a></p>
            </footer>
        </article>

        <!-- Related articles --&gt;
        <section>
            <h2>Related Articles</h2>
            <article>
                <h3><a href="/article-2">Another Article</a></h3>
                <p>Brief description...</p>
            </article>
        </section>
    </main>

    <!-- Sidebar --&gt;
    <aside>
        <section>
            <h2>About the Author</h2>
            <p>Bio information...</p>
        </section>
        
        <section>
            <h2>Popular Posts</h2>
            <ul>
                <li><a href="/post-1">Popular Post 1</a></li>
                <li><a href="/post-2">Popular Post 2</a></li>
            </ul>
        </section>
    </aside>

    <!-- Site footer --&gt;
    <footer>
        <div class="footer-content">
            <section>
                <h3>About Us</h3>
                <p>Company info...</p>
            </section>
            
            <section>
                <h3>Quick Links</h3>
                <ul>
                    <li><a href="/privacy">Privacy</a></li>
                    <li><a href="/terms">Terms</a></li>
                </ul>
            </section>
        </div>
        
        <p>&copy; 2025 My Website. All rights reserved.</p>
    </footer>
</body>
</html>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Document Outline</h2>
                <p>
                    Screen readers and browsers create a document outline from your semantic structure:
                </p>

                <div className="outline-visualization">
                    <CodeBlock language="text" code={`Page: My Website
 Navigation: Main navigation
 Main Content
    Navigation: Breadcrumb
    Article: Understanding Semantic HTML
       Header
       Content
       Footer
    Section: Related Articles
        Article: Another Article
 Aside
    Section: About the Author
    Section: Popular Posts
 Footer`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use ONE <code>&lt;main&gt;</code> per page</li>
                            <li>Use semantic elements over <code>&lt;div&gt;</code> when possible</li>
                            <li>Include <code>aria-label</code> for multiple navs</li>
                            <li>Add skip links for keyboard users</li>
                            <li>Nest headers inside <code>&lt;article&gt;</code> for metadata</li>
                            <li>Test with screen readers to verify structure</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Nest <code>&lt;main&gt;</code> inside other semantic elements</li>
                            <li>Use <code>&lt;nav&gt;</code> for every group of links</li>
                            <li>Put entire page in <code>&lt;article&gt;</code></li>
                            <li>Use semantic elements just for styling</li>
                            <li>Ignore heading hierarchy (h1, then h2, then h3...)</li>
                            <li>Use <code>&lt;div&gt;</code> when semantic element exists</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLLayout;


